import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { API_BASE } from '../config/api';
import { resolveSession } from '../lib/session';
import { useSessionUser } from '../SessionContext';
import './Reports.css';

const PAGE_SIZE = 100;
const DEFAULT_THRESHOLD_SECONDS = 30;

const emptyMetric = {
  totalCalls: 0,
  dropInIvr: 0,
  dropInQueue: 0,
  totalAbandoned: 0,
  abandonedBeforeThreshold: 0,
  abandonedAfterThreshold: 0,
  totalAnswered: 0,
  answeredBeforeThreshold: 0,
  answeredAfterThreshold: 0,
};

const DEFAULT_SQL = `WITH call_events AS (
  SELECT
    COALESCE(NULLIF(uniqueid, ''), NULLIF(channelid, ''), id::text) AS call_id,
    created_at AS event_at,
    created_at::date AS report_date,
    NULLIF(callernumber, '') AS cli,
    NULLIF(route_point_number, '') AS dnis,
    LOWER(TRIM(COALESCE(direction, ''))) AS direction,
    LOWER(COALESCE(queuestatus, '')) AS queue_status,
    LOWER(COALESCE(disposition, '')) AS disposition,
    LOWER(COALESCE(userfieldraw, '')) AS user_event,
    COALESCE(ivrtime, 0)::integer AS ivr_seconds,
    COALESCE(queueholdtime, 0)::integer AS queue_hold_seconds,
    COALESCE(ringtime, 0)::integer AS ring_seconds,
    COALESCE(oncalltime, 0)::integer AS talk_seconds,
    COALESCE(totalduration, 0)::integer AS total_duration_seconds,
    oncallat,
    answeredby
  FROM call_state_log
  WHERE created_at >= :start_date::date
    AND created_at < (:end_date::date + INTERVAL '1 day')
    AND LOWER(TRIM(COALESCE(direction, ''))) = 'inbound'
),
logical_calls AS (
  SELECT
    call_id,
    MIN(event_at) AS started_at,
    MAX(event_at) AS ended_at,
    MIN(report_date) AS report_date,
    MAX(cli) AS cli,
    MAX(dnis) AS dnis,
    MAX(direction) AS direction,
    MAX(answeredby) AS answered_by,
    MAX(ivr_seconds) AS ivr_seconds,
    MAX(queue_hold_seconds) AS queue_hold_seconds,
    MAX(ring_seconds) AS ring_seconds,
    MAX(talk_seconds) AS talk_seconds,
    MAX(total_duration_seconds) AS total_duration_seconds,
    MAX(CASE WHEN queue_status <> '' OR user_event LIKE 'event_queue%' THEN 1 ELSE 0 END) AS hit_queue,
    MAX(CASE
      WHEN oncallat IS NOT NULL
        OR talk_seconds > 0
        OR queue_status IN ('answered', 'answer', 'connected', 'complete', 'completed')
        OR disposition = 'answered'
      THEN 1 ELSE 0
    END) AS answered
  FROM call_events
  WHERE call_id IS NOT NULL
  GROUP BY call_id
)
SELECT
  report_date,
  COUNT(*) AS total_calls,
  SUM(CASE WHEN answered = 0 AND hit_queue = 0 THEN 1 ELSE 0 END) AS drop_in_ivr,
  SUM(CASE WHEN answered = 0 AND hit_queue = 1 THEN 1 ELSE 0 END) AS drop_in_queue,
  SUM(CASE WHEN answered = 0 AND hit_queue = 1 THEN 1 ELSE 0 END) AS total_abandoned,
  SUM(CASE WHEN answered = 0 AND hit_queue = 1 AND queue_hold_seconds <= :threshold_seconds THEN 1 ELSE 0 END) AS abandoned_before_threshold,
  SUM(CASE WHEN answered = 0 AND hit_queue = 1 AND queue_hold_seconds > :threshold_seconds THEN 1 ELSE 0 END) AS abandoned_after_threshold,
  SUM(answered) AS total_answered,
  SUM(CASE WHEN answered = 1 AND queue_hold_seconds <= :threshold_seconds THEN 1 ELSE 0 END) AS answered_before_threshold,
  SUM(CASE WHEN answered = 1 AND queue_hold_seconds > :threshold_seconds THEN 1 ELSE 0 END) AS answered_after_threshold
FROM logical_calls
GROUP BY report_date
ORDER BY report_date ASC;`;

const summaryColumns = [
  { key: 'totalCalls', label: 'Total Calls' },
  { key: 'dropInIvr', label: 'Drop in IVR' },
  { key: 'dropInQueue', label: 'Drop in Queue Total' },
  { key: 'abandonedBeforeThreshold', label: 'Before Threshold Abandoned' },
  { key: 'abandonedAfterThreshold', label: 'After Threshold Abandoned' },
  { key: 'totalAbandoned', label: 'Total Abandoned' },
  { key: 'answeredBeforeThreshold', label: 'Before Threshold Answer' },
  { key: 'answeredAfterThreshold', label: 'After Threshold Answer' },
  { key: 'totalAnswered', label: 'Total Answer' },
];

const detailColumns = [
  { key: 'date', label: 'Date', type: 'date' },
  { key: 'time', label: 'Time' },
  { key: 'cli', label: 'CLI' },
  { key: 'dnis', label: 'DNIS' },
  { key: 'uniqueId', label: 'Unique ID' },
  { key: 'direction', label: 'Direction' },
  { key: 'queueStatus', label: 'Queue Status' },
  { key: 'answeredBy', label: 'Answered By' },
  { key: 'ivrSeconds', label: 'IVR Time', type: 'time' },
  { key: 'totalWaitSeconds', label: 'Total Wait Time', type: 'time' },
  { key: 'ringSeconds', label: 'Ring Time', type: 'time' },
  { key: 'totalOnCallSeconds', label: 'Total Oncall Time', type: 'time' },
  { key: 'totalDurationSeconds', label: 'Total Duration', type: 'time' },
  { key: 'outcome', label: 'Outcome' },
  { key: 'thresholdBucket', label: 'Threshold Bucket' },
];

const toIsoDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const todayIso = () => toIsoDate(new Date());

const monthStartIso = () => {
  const date = new Date();
  date.setDate(1);
  return toIsoDate(date);
};

const normalizeText = (value) => String(value ?? '').trim();

const readNumber = (source, keys, fallback = 0) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return Number(value) || fallback;
    }
  }
  return fallback;
};

const formatDate = (value) => {
  const text = normalizeText(value);
  if (!text) return '';
  const [year, month, day] = text.slice(0, 10).split('-');
  if (!year || !month || !day) return text;
  return `${Number(month)}/${Number(day)}/${year}`;
};

const formatNumber = (value) => Math.round(Number(value) || 0).toLocaleString();

const formatHms = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remSeconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remSeconds).padStart(2, '0')}`;
};

const formatTime = (value) => {
  const text = normalizeText(value);
  if (!text) return '';
  const date = new Date(text);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleTimeString('en-US', { hour12: false });
  }
  return text.slice(11, 19) || text;
};

const normalizeMetric = (source = {}) => ({
  totalCalls: readNumber(source, ['totalCalls', 'total_calls', 'TotalCalls', 'offered', 'Offered']),
  dropInIvr: readNumber(source, ['dropInIvr', 'drop_in_ivr', 'DropInIvr', 'ivrDropped', 'ivr_dropped']),
  dropInQueue: readNumber(source, ['dropInQueue', 'drop_in_queue', 'DropInQueue', 'queueDropped', 'queue_dropped']),
  totalAbandoned: readNumber(source, ['totalAbandoned', 'total_abandoned', 'TotalAbandoned', 'abandoned', 'Abandoned']),
  abandonedBeforeThreshold: readNumber(source, ['abandonedBeforeThreshold', 'abandoned_before_threshold', 'AbandonedBeforeThreshold', 'beforeThresholdAbandoned']),
  abandonedAfterThreshold: readNumber(source, ['abandonedAfterThreshold', 'abandoned_after_threshold', 'AbandonedAfterThreshold', 'afterThresholdAbandoned']),
  totalAnswered: readNumber(source, ['totalAnswered', 'total_answered', 'TotalAnswered', 'answered', 'Answered']),
  answeredBeforeThreshold: readNumber(source, ['answeredBeforeThreshold', 'answered_before_threshold', 'AnsweredBeforeThreshold', 'beforeThresholdAnswer']),
  answeredAfterThreshold: readNumber(source, ['answeredAfterThreshold', 'answered_after_threshold', 'AnsweredAfterThreshold', 'afterThresholdAnswer']),
});

const normalizeSummaryRow = (source = {}) => ({
  date: normalizeText(source.date ?? source.reportDate ?? source.report_date ?? source.ReportDate ?? ''),
  ...normalizeMetric(source),
});

const deriveOutcome = (row = {}, thresholdSeconds = DEFAULT_THRESHOLD_SECONDS) => {
  const explicit = normalizeText(row.outcome ?? row.Outcome);
  if (explicit) return explicit;

  const answered = Boolean(row.answered || row.Answered || row.totalAnswered || row.TotalAnswered);
  const hitQueue = Boolean(row.hitQueue || row.hit_queue || row.HitQueue);
  const queueHoldSeconds = readNumber(row, ['queueHoldSeconds', 'queue_hold_seconds', 'QueueHoldSeconds', 'QueueHoldTime']);

  if (answered) return queueHoldSeconds <= thresholdSeconds ? 'Answered Before Threshold' : 'Answered After Threshold';
  if (hitQueue) return queueHoldSeconds <= thresholdSeconds ? 'Abandoned Before Threshold' : 'Abandoned After Threshold';
  return 'Drop in IVR';
};

const normalizeDetailRow = (source = {}, thresholdSeconds = DEFAULT_THRESHOLD_SECONDS) => {
  const startedAt = normalizeText(
    source.startedAt ?? source.started_at ?? source.StartedAt ??
    source.callStartedAt ?? source.call_started_at ?? source.CallStartedAt ??
    source.createdAt ?? source.created_at ?? source.CreatedAt
  );
  const queueHoldSeconds = readNumber(source, ['queueHoldSeconds', 'queue_hold_seconds', 'QueueHoldSeconds', 'QueueHoldTime']);
  const ivrSeconds = readNumber(source, ['ivrSeconds', 'ivr_seconds', 'IvrSeconds', 'IvrTime']);
  const ringSeconds = readNumber(source, ['ringSeconds', 'ring_seconds', 'RingSeconds', 'RingTime']);
  const talkSeconds = readNumber(source, ['talkSeconds', 'talk_seconds', 'TalkSeconds', 'OnCallTime']);
  const outcome = deriveOutcome(source, thresholdSeconds);
  const thresholdBucket = normalizeText(source.thresholdBucket ?? source.threshold_bucket ?? source.ThresholdBucket) ||
    (outcome.toLowerCase().includes('before') ? `Before ${thresholdSeconds}s` :
      outcome.toLowerCase().includes('after') ? `After ${thresholdSeconds}s` : '');

  return {
    date: normalizeText(source.date ?? source.reportDate ?? source.report_date ?? source.ReportDate ?? startedAt.slice(0, 10)),
    time: normalizeText(source.time ?? source.reportTime ?? source.report_time ?? source.ReportTime) || formatTime(startedAt),
    cli: normalizeText(source.cli ?? source.CLI ?? source.callerNumber ?? source.caller_number ?? source.callernumber ?? source.CallerNumber),
    dnis: normalizeText(source.dnis ?? source.DNIS ?? source.routePointNumber ?? source.route_point_number ?? source.RoutePointNumber ?? source.dnisNumber ?? source.dnis_number ?? source.DnisNumber),
    uniqueId: normalizeText(source.uniqueId ?? source.unique_id ?? source.UniqueId ?? source.callId ?? source.call_id),
    direction: normalizeText(source.direction ?? source.Direction),
    queueStatus: normalizeText(source.queueStatus ?? source.queue_status ?? source.QueueStatus),
    answeredBy: normalizeText(source.answeredBy ?? source.answered_by ?? source.AnsweredBy),
    ivrSeconds,
    queueHoldSeconds,
    ringSeconds,
    talkSeconds,
    totalWaitSeconds: readNumber(source, ['totalWaitSeconds', 'total_wait_seconds', 'TotalWaitSeconds', 'TotalWaitTime'], ivrSeconds + queueHoldSeconds + ringSeconds),
    totalOnCallSeconds: readNumber(source, ['totalOnCallSeconds', 'total_on_call_seconds', 'TotalOnCallSeconds', 'TotalOnCallTime'], talkSeconds),
    totalDurationSeconds: readNumber(source, ['totalDurationSeconds', 'total_duration_seconds', 'TotalDurationSeconds', 'TotalDuration']),
    outcome,
    thresholdBucket,
  };
};

const summarizeDetails = (rows = [], thresholdSeconds = DEFAULT_THRESHOLD_SECONDS) => {
  const totals = { ...emptyMetric };
  rows.forEach((row) => {
    const outcome = normalizeText(row.outcome).toLowerCase();
    totals.totalCalls += 1;
    if (outcome.includes('drop in ivr')) totals.dropInIvr += 1;
    if (outcome.includes('abandoned') || outcome.includes('drop in queue')) {
      totals.dropInQueue += 1;
      totals.totalAbandoned += 1;
      if ((Number(row.queueHoldSeconds) || 0) <= thresholdSeconds) {
        totals.abandonedBeforeThreshold += 1;
      } else {
        totals.abandonedAfterThreshold += 1;
      }
    }
    if (outcome.includes('answered')) {
      totals.totalAnswered += 1;
      if ((Number(row.queueHoldSeconds) || 0) <= thresholdSeconds) {
        totals.answeredBeforeThreshold += 1;
      } else {
        totals.answeredAfterThreshold += 1;
      }
    }
  });
  return totals;
};

const summarizeByDate = (rows = [], thresholdSeconds = DEFAULT_THRESHOLD_SECONDS) => {
  const groups = new Map();
  rows.forEach((row) => {
    const key = row.date || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return Array.from(groups.entries())
    .map(([date, groupRows]) => ({ date, ...summarizeDetails(groupRows, thresholdSeconds) }))
    .sort((left, right) => String(left.date).localeCompare(String(right.date)));
};

const sumSummaryRows = (rows = []) => rows.reduce((acc, row) => {
  summaryColumns.forEach((column) => {
    acc[column.key] = (Number(acc[column.key]) || 0) + (Number(row[column.key]) || 0);
  });
  return acc;
}, { ...emptyMetric });

const summaryCells = (metric = emptyMetric) => summaryColumns.map((column) => formatNumber(metric[column.key]));

const formatDetailCell = (row, column) => {
  const value = row?.[column.key] ?? '';
  if (column.type === 'date') return formatDate(value);
  if (column.type === 'time') return formatHms(value);
  return value;
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const buildReportPath = (filters, page = 1) => {
  const params = new URLSearchParams();
  params.set('startDate', filters.startDate);
  params.set('endDate', filters.endDate);
  params.set('thresholdSeconds', String(filters.thresholdSeconds || DEFAULT_THRESHOLD_SECONDS));
  params.set('page', String(page));
  params.set('perPage', String(PAGE_SIZE));
  if (filters.cli) params.set('cli', filters.cli);
  if (filters.dnis) params.set('dnis', filters.dnis);
  return `/reports/daily-call-report?${params.toString()}`;
};

const fetchJson = async (path) => {
  const response = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  const text = await response.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok || json?.ok === false || json?.success === false) {
    const message = json?.error || json?.message || text || `Request failed: ${response.status}`;
    if (response.status === 404 && String(message).toLowerCase().includes('route')) {
      throw new Error('Laravel report API route missing. Register GET /api/reports/daily-call-report in routes/api.php.');
    }
    throw new Error(message);
  }
  return json;
};

const DailyCallReport = () => {
  const navigate = useNavigate();
  const { user: sessionUser } = useSessionUser();
  const { username, roleName } = useMemo(() => resolveSession(sessionUser), [sessionUser]);
  const [filters, setFilters] = useState({
    startDate: monthStartIso(),
    endDate: todayIso(),
    thresholdSeconds: DEFAULT_THRESHOLD_SECONDS,
    cli: '',
    dnis: '',
  });
  const [report, setReport] = useState({
    totals: emptyMetric,
    summaryRows: [],
    detail: { rows: [], allRows: [], page: 1, perPage: PAGE_SIZE, totalRows: 0, totalPages: 1 },
    reportInterval: '',
    source: 'none',
    dataIssues: [],
    sqlOutput: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const thresholdSeconds = Number(filters.thresholdSeconds) || DEFAULT_THRESHOLD_SECONDS;

  const loadReport = useCallback(async (nextFilters = filters, page = 1) => {
    const threshold = Number(nextFilters.thresholdSeconds) || DEFAULT_THRESHOLD_SECONDS;
    if (!nextFilters.startDate || !nextFilters.endDate) {
      setError('Start Date and End Date are required.');
      return;
    }
    if (nextFilters.startDate > nextFilters.endDate) {
      setError('Start Date cannot be after End Date.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const json = await fetchJson(buildReportPath(nextFilters, page));
      const root = json?.data || json || {};
      const detail = root.detail || {};
      const sourceDetailRows =
        Array.isArray(detail.rows) ? detail.rows :
        Array.isArray(root.detailRows) ? root.detailRows :
        Array.isArray(root.rows) ? root.rows :
        [];
      const sourceAllDetailRows =
        Array.isArray(detail.allRows) ? detail.allRows :
        Array.isArray(root.allDetailRows) ? root.allDetailRows :
        Array.isArray(root.allRows) ? root.allRows :
        sourceDetailRows;
      const detailRows = sourceDetailRows.map((row) => normalizeDetailRow(row, threshold));
      const allDetailRows = sourceAllDetailRows.map((row) => normalizeDetailRow(row, threshold));
      const sourceSummaryRows =
        Array.isArray(root.summaryRows) ? root.summaryRows :
        Array.isArray(root.summary?.rows) ? root.summary.rows :
        [];
      const summaryRows = sourceSummaryRows.length
        ? sourceSummaryRows.map(normalizeSummaryRow)
        : summarizeByDate(allDetailRows, threshold);
      const totals = root.totals ? normalizeMetric(root.totals) : sumSummaryRows(summaryRows);

      setReport({
        totals,
        summaryRows,
        detail: {
          rows: detailRows,
          allRows: allDetailRows,
          page: Number(detail.page || page),
          perPage: Number(detail.perPage || PAGE_SIZE),
          totalRows: Number(detail.totalRows || allDetailRows.length || detailRows.length),
          totalPages: Number(detail.totalPages || 1),
        },
        reportInterval: root.reportInterval || '',
        source: normalizeText(root.source || 'call_state_log'),
        dataIssues: Array.isArray(root.dataIssues) ? root.dataIssues : [],
        sqlOutput: root.sqlOutput || '',
      });
    } catch (err) {
      setReport((prev) => ({
        ...prev,
        totals: emptyMetric,
        summaryRows: [],
        detail: { rows: [], allRows: [], page: 1, perPage: PAGE_SIZE, totalRows: 0, totalPages: 1 },
      }));
      setError(err?.message || 'Failed to load Daily Call Report.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    document.body.setAttribute('data-app', 'reports');
    return () => document.body.removeAttribute('data-app');
  }, []);

  useEffect(() => {
    loadReport(filters, 1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value.trimStart() }));
  };

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const summaryRows = [
      ['Daily Call Report'],
      ['Report Interval', report.reportInterval || `${filters.startDate} - ${filters.endDate}`],
      ['Data Source', report.source],
      ['Threshold Seconds', thresholdSeconds],
      ['CLI', filters.cli || 'All'],
      ['DNIS', filters.dnis || 'All'],
      [],
      ['Grand Total', ...summaryColumns.map((column) => column.label)],
      ['', ...summaryColumns.map((column) => Number(report.totals[column.key] || 0))],
      [],
      ['Date', ...summaryColumns.map((column) => column.label)],
      ...report.summaryRows.map((row) => [
        formatDate(row.date),
        ...summaryColumns.map((column) => Number(row[column.key] || 0)),
      ]),
    ];
    const detailRows = [
      detailColumns.map((column) => column.label),
      ...(report.detail?.allRows || []).map((row) => detailColumns.map((column) => formatDetailCell(row, column))),
    ];

    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(detailRows), 'Record Detail');
    XLSX.writeFile(workbook, `daily-call-report-${filters.startDate}-${filters.endDate}.xlsx`);
  };

  const currentPage = Number(report.detail?.page || 1);
  const totalPages = Number(report.detail?.totalPages || 1);
  const totalRows = Number(report.detail?.totalRows || 0);

  return (
    <div className="cdn-report-shell daily-call-report-shell">
      <header className="cdn-report-topbar no-print">
        <div>
          <strong>SLACC</strong>
          <span>Sindh Legal Advisory Call Center</span>
        </div>
        <div>
          <h1>Daily Call Report</h1>
          <p>Database report</p>
        </div>
        <div className="cdn-report-user-actions">
          <span>{username ? `${username}${roleName ? ` - ${roleName}` : ''}` : 'Report Module'}</span>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports')}>CDN Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/dnis-statistics')}>DNIS Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-state-report')}>Agent State</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-login-logout-report')}>Login/Logout</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-performance-report')}>Performance</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/backoffice')}>Back Office</button>
          <button type="button" className="cdn-btn danger" onClick={() => navigate('/logout')}>Logout</button>
        </div>
      </header>

      <section className="cdn-filter-panel no-print">
        <div className="daily-call-filter-grid">
          <label><span>Start Date</span><input type="date" name="startDate" value={filters.startDate} onChange={updateField} /></label>
          <label><span>End Date</span><input type="date" name="endDate" value={filters.endDate} onChange={updateField} /></label>
          <label><span>Threshold Seconds</span><input type="number" min="1" name="thresholdSeconds" value={filters.thresholdSeconds} onChange={updateField} /></label>
          <label><span>CLI</span><input type="text" name="cli" value={filters.cli} onChange={updateField} placeholder="All CLI" /></label>
          <label><span>DNIS</span><input type="text" name="dnis" value={filters.dnis} onChange={updateField} placeholder="All DNIS" /></label>
          <div className="cdn-filter-actions">
            <button type="button" className="cdn-btn primary" onClick={() => loadReport(filters, 1)} disabled={loading}>
              {loading ? 'Loading' : 'Show'}
            </button>
            <button type="button" className="cdn-btn success" onClick={exportExcel} disabled={!report.detail?.allRows?.length}>Excel</button>
            <button type="button" className="cdn-btn soft" onClick={() => window.print()} disabled={!report.detail?.allRows?.length}>PDF</button>
          </div>
        </div>
        {error ? <div className="cdn-alert">{error}</div> : null}
        {report.dataIssues?.length ? (
          <div className="dnis-data-issues">
            {report.dataIssues.map((issue) => <p key={issue}>{issue}</p>)}
          </div>
        ) : null}
      </section>

      <main className="cdn-report-paper daily-call-report-paper">
        <section className="cdn-report-heading">
          <h2>Daily Call Report</h2>
          <p><strong>Report Interval:</strong> {report.reportInterval || `${filters.startDate} 00:00:00 - ${filters.endDate} 23:59:59`}</p>
          <p><strong>Threshold:</strong> {thresholdSeconds} seconds</p>
          <p><strong>CLI / DNIS:</strong> {filters.cli || 'All'} / {filters.dnis || 'All'}</p>
        </section>

        <section className="daily-call-kpi-grid">
          <article><span>Total Calls</span><strong>{formatNumber(report.totals.totalCalls)}</strong></article>
          <article><span>Drop in IVR</span><strong>{formatNumber(report.totals.dropInIvr)}</strong></article>
          <article><span>Drop in Queue</span><strong>{formatNumber(report.totals.dropInQueue)}</strong></article>
          <article><span>Total Abandoned</span><strong>{formatNumber(report.totals.totalAbandoned)}</strong></article>
          <article><span>Total Answer</span><strong>{formatNumber(report.totals.totalAnswered)}</strong></article>
          <article><span>Before Th Answer</span><strong>{formatNumber(report.totals.answeredBeforeThreshold)}</strong></article>
        </section>

        <section className="daily-call-table-section">
          <h3 className="daily-call-section-title">Summary</h3>
          <table className="daily-call-report-table daily-call-summary-table">
            <thead>
              <tr className="cdn-total-row">
                <th>Grand Total</th>
                {summaryColumns.map((column, index) => <th key={column.key}>{summaryCells(report.totals)[index]}</th>)}
              </tr>
              <tr>
                <th>Date</th>
                {summaryColumns.map((column) => <th key={column.key}>{column.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {report.summaryRows.length ? report.summaryRows.map((row) => (
                <tr key={row.date}>
                  <td>{formatDate(row.date)}</td>
                  {summaryColumns.map((column) => <td key={column.key}>{formatNumber(row[column.key])}</td>)}
                </tr>
              )) : (
                <tr>
                  <td colSpan={summaryColumns.length + 1} className="cdn-empty">
                    {loading ? 'Loading summary...' : 'No summary data found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="daily-call-table-section">
          <h3 className="daily-call-section-title">Record Detail</h3>
          <table className="daily-call-report-table daily-call-detail-table">
            <thead>
              <tr>
                {detailColumns.map((column) => <th key={column.key}>{column.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {report.detail?.rows?.length ? report.detail.rows.map((row, index) => (
                <tr key={`${row.uniqueId || row.cli || 'call'}-${row.date}-${row.time}-${index}`}>
                  {detailColumns.map((column) => <td key={column.key}>{formatDetailCell(row, column)}</td>)}
                </tr>
              )) : (
                <tr>
                  <td colSpan={detailColumns.length} className="cdn-empty">
                    {loading ? 'Loading record detail...' : 'No record detail found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </main>

      <section className="cdn-pager no-print">
        <button type="button" className="cdn-btn soft" disabled={loading || currentPage <= 1} onClick={() => loadReport(filters, currentPage - 1)}>Previous</button>
        <span>Page {currentPage} of {totalPages} - {formatNumber(totalRows)} detail records</span>
        <button type="button" className="cdn-btn soft" disabled={loading || currentPage >= totalPages} onClick={() => loadReport(filters, currentPage + 1)}>Next</button>
      </section>

      <section className="cdn-sql-panel no-print">
        <details>
          <summary>Database SQL Output</summary>
          <pre>{report.sqlOutput || DEFAULT_SQL}</pre>
        </details>
      </section>
    </div>
  );
};

export default DailyCallReport;
